import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { StatusBadge } from './StatusBadge';
import { PriorityBadge } from './PriorityBadge';
import { PlatformChip } from './PlatformChip';
import { RowActionsMenu } from './RowActionsMenu';
import { LIST_ITEM } from '../lib/motion';
import { formatSource, getInitials } from '../lib/leadMeta';
import type { Lead } from '@/types/api';

interface LeadRowProps {
  lead: Lead;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onUpdate: (updates: Partial<Lead>) => void;
  onDelete: () => void;
}

/** Desktop table row. Hidden below `md`; see `LeadCard` for the mobile equivalent. */
export function LeadRow({ lead, isSelected, onSelect, onUpdate, onDelete }: LeadRowProps) {
  const createdAt = new Date(lead.createdAt);

  return (
    <motion.tr
      variants={LIST_ITEM}
      exit="exit"
      className={cn(
        'border-b border-border/70 text-sm transition-colors last:border-b-0',
        isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
      )}
    >
      <td className="w-10 px-3 py-2.5">
        <Checkbox checked={isSelected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Select ${lead.customerName}`} />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {getInitials(lead.customerName)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{lead.customerName}</p>
            <p className="max-w-[28ch] truncate text-xs text-muted-foreground">{lead.message}</p>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <StatusBadge status={lead.status} />
      </td>
      <td className="px-3 py-2.5">
        <PriorityBadge priority={lead.priority} />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <PlatformChip platform={lead.platform} />
          <span className="max-w-[14ch] truncate text-xs text-muted-foreground">{formatSource(lead.source)}</span>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5">
        <span className="text-xs text-muted-foreground" title={format(createdAt, 'PPpp')}>
          {formatDistanceToNow(createdAt, { addSuffix: true })}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right">
        <RowActionsMenu
          label={lead.customerName}
          onMarkContacted={() => onUpdate({ status: 'contacted' })}
          onMarkQualified={() => onUpdate({ status: 'qualified' })}
          onSetHighPriority={() => onUpdate({ priority: 'high' })}
          onDelete={onDelete}
        />
      </td>
    </motion.tr>
  );
}
